/**
 * Enrollment Modal Component
 * 
 * Confirmation modal for enrolling in a cohort.
 */

import { useState } from 'react';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import Alert from '@cloudscape-design/components/alert';

interface EnrollmentModalProps {
  visible: boolean;
  cohortName: string;
  cohortDescription: string;
  startDate: string;
  endDate: string;
  onDismiss: () => void;
  onConfirm: () => Promise<void>;
}

export function EnrollmentModal({
  visible,
  cohortName,
  cohortDescription,
  startDate,
  endDate,
  onDismiss,
  onConfirm,
}: EnrollmentModalProps) {
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setIsEnrolling(true);
    setError(null);

    try {
      await onConfirm();
      onDismiss();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enroll in cohort');
    } finally {
      setIsEnrolling(false);
    }
  };

  const handleDismiss = () => {
    if (!isEnrolling) {
      setError(null);
      onDismiss();
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      header="Enroll in Cohort"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleDismiss} disabled={isEnrolling}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              loading={isEnrolling}
              disabled={isEnrolling}
            >
              Enroll Now
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {error && (
          <Alert type="error" dismissible onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box variant="p">
          You are about to enroll in <strong>{cohortName}</strong>.
        </Box>

        <Box variant="p" color="text-body-secondary">
          {cohortDescription}
        </Box>

        <Alert type="info" header="Cohort Details">
          <SpaceBetween size="xs">
            <Box>
              <strong>Start Date:</strong>{' '}
              {new Date(startDate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </Box>
            <Box>
              <strong>End Date:</strong>{' '}
              {new Date(endDate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </Box>
          </SpaceBetween>
        </Alert>

        <Box variant="p" color="text-body-secondary">
          By enrolling, you commit to completing the required activities and participating
          actively in the cohort.
        </Box>
      </SpaceBetween>
    </Modal>
  );
}
